/**
 * Product Catalog Component
 * 
 * Displays list of all products with key status and revenue information.
 * Shows:
 * - Product name
 * - Product status
 * - Product revenue
 * 
 * Used at the Business (company) level dashboard.
 */

import React, { useState } from 'react';
import { Table, Tag, Input, Select, Tooltip, Progress, Empty, Button } from 'antd';
import {
  ShopOutlined,
  SearchOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps } from './BaseReportingComponent';
import { fetchProductCatalogFromLocalBridge } from '../../../services/bridgeSessionDataAdapter';
import { isDriveFlowReportingEnabled } from '../../../services/driveFlowAdapter';
import styles from './ProductCatalogComponent.module.css';

interface Product {
  id: string;
  name: string;
  status: string;
  revenue: number;
  category?: string;
  launchDate?: string;
}

interface ProductCatalogComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Whether to show search and filters */
  showFilters?: boolean;
  /** Whether to use compact table view */
  compact?: boolean;
  /** Callback when product is selected (legacy, prefer onDrillDown) */
  onProductSelect?: (productId: string) => void;
  /** Whether to show the Add Product button */
  showAddButton?: boolean;
  /** Callback when Add Product is clicked */
  onAddProduct?: () => void;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const getStatusConfig = (status: string): { color: string; icon: React.ReactNode; label: string } => {
  const statusLower = status?.toLowerCase() || '';
  
  switch (statusLower) {
    case 'active':
      return { color: 'green', icon: <CheckCircleOutlined />, label: 'Active' };
    case 'development':
      return { color: 'blue', icon: <ClockCircleOutlined />, label: 'Development' };
    case 'beta':
      return { color: 'orange', icon: <ExclamationCircleOutlined />, label: 'Beta' };
    case 'concept':
      return { color: 'purple', icon: <ClockCircleOutlined />, label: 'Concept' };
    case 'sunset':
      return { color: 'gold', icon: <ExclamationCircleOutlined />, label: 'Sunset' };
    case 'discontinued':
      return { color: 'red', icon: <StopOutlined />, label: 'Discontinued' };
    default:
      return { color: 'default', icon: <ShopOutlined />, label: status || 'Unknown' };
  }
};

const extractProducts = (data: ComponentData): Product[] => {
  // Products can come from child_data or from historical_data items
  const products: Product[] = [];
  
  // Check child_data for product artifacts
  if (data.child_data && data.child_data.product_catalog) {
    data.child_data.product_catalog.forEach((item: any) => {
      products.push({
        id: item.artifact_id,
        name: item.artifact_name || item.data?.product_name || 'Unknown Product',
        status: item.data?.product_status || 'unknown',
        revenue: item.data?.product_revenue || 0,
        category: item.data?.product_category,
        launchDate: item.data?.launch_date
      });
    });
  }
  
  // If no child products, create a single entry from current data
  if (products.length === 0 && data.data.product_name) {
    products.push({
      id: data.artifact_id,
      name: data.data.product_name,
      status: data.data.product_status || 'unknown',
      revenue: data.data.product_revenue || 0,
      category: data.data.product_category,
      launchDate: data.data.launch_date
    });
  }

  // Also check historical data for products
  data.historical_data.forEach((item) => {
    if (item.values.product_name && !products.some(p => p.name === item.values.product_name)) {
      products.push({
        id: item.item_id,
        name: item.values.product_name,
        status: item.values.product_status || 'unknown',
        revenue: item.values.product_revenue || 0,
        category: item.values.product_category,
        launchDate: item.values.launch_date
      });
    }
  });

  return products;
};

export const ProductCatalogComponent: React.FC<ProductCatalogComponentProps> = ({
  showFilters = true,
  compact = false,
  onProductSelect,
  onDrillDown,
  showAddButton = false,
  onAddProduct,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Handle product selection - prefer onDrillDown, fall back to onProductSelect
  const handleProductClick = (productId: string, productName: string) => {
    if (onDrillDown) {
      onDrillDown(productId, productName);
    } else if (onProductSelect) {
      onProductSelect(productId);
    }
  };

  const isClickable = !!onDrillDown || !!onProductSelect;

  const renderContent = (data: ComponentData, loading: boolean) => {
    const products = extractProducts(data);
    
    // Apply filters
    let filteredProducts = products;
    if (searchTerm) {
      filteredProducts = filteredProducts.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter) {
      filteredProducts = filteredProducts.filter(p => 
        p.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Calculate totals
    const totalRevenue = products.reduce((sum, p) => sum + (p.revenue || 0), 0);
    const maxRevenue = Math.max(...products.map(p => p.revenue || 0), 1);

    // Get unique statuses for filter
    const statuses = Array.from(new Set(products.map(p => p.status))).filter(Boolean);

    const columns = [
      {
        title: 'Product',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, record: Product) => (
          <div 
            className={styles.productName}
            onClick={() => handleProductClick(record.id, record.name)}
            style={{ cursor: isClickable ? 'pointer' : 'default' }}
          >
            <ShopOutlined className={styles.productIcon} />
            <span>{name}</span>
          </div>
        ),
        sorter: (a: Product, b: Product) => a.name.localeCompare(b.name)
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: string) => {
          const config = getStatusConfig(status);
          return (
            <Tag color={config.color} icon={config.icon}>
              {config.label}
            </Tag>
          );
        },
        filters: statuses.map(s => ({ text: s, value: s })),
        onFilter: (value: any, record: Product) => record.status === value
      },
      {
        title: 'Revenue',
        dataIndex: 'revenue',
        key: 'revenue',
        width: 200,
        render: (revenue: number) => (
          <div className={styles.revenueCell}>
            <Tooltip title={formatCurrency(revenue)}>
              <Progress
                percent={Math.round((revenue / maxRevenue) * 100)}
                size="small"
                strokeColor="#10b981"
                showInfo={false}
                className={styles.revenueBar}
              />
            </Tooltip>
            <span className={styles.revenueValue}>
              {formatCurrency(revenue)}
            </span>
          </div>
        ),
        sorter: (a: Product, b: Product) => (a.revenue || 0) - (b.revenue || 0),
        defaultSortOrder: 'descend' as const
      }
    ];

    if (products.length === 0) {
      return (
        <div className={styles.productCatalog}>
          <div className={styles.header}>
            <h3 className={styles.title}>
              <ShopOutlined /> Product Catalog
            </h3>
          </div>
          <Empty description="No products found" />
        </div>
      );
    }

    return (
      <div className={`${styles.productCatalog} ${compact ? styles.compact : ''}`}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h3 className={styles.title}>
              <ShopOutlined /> Product Catalog
            </h3>
            <span className={styles.productCount}>
              {products.length} product{products.length !== 1 ? 's' : ''}
            </span>
          </div>
          
          <div className={styles.headerActions}>
            <div className={styles.totalRevenue}>
              <DollarOutlined />
              <span>Total: {formatCurrency(totalRevenue)}</span>
            </div>
            {showAddButton && onAddProduct && (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddProduct();
                }}
                className={styles.addButton}
              >
                Add Product
              </Button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className={styles.filters}>
            <Input
              placeholder="Search products..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className={styles.searchInput}
              allowClear
            />
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              className={styles.statusFilter}
            >
              {statuses.map(status => {
                const config = getStatusConfig(status);
                return (
                  <Select.Option key={status} value={status}>
                    <Tag color={config.color}>{config.label}</Tag>
                  </Select.Option>
                );
              })}
            </Select>
          </div>
        )}

        <Table
          dataSource={filteredProducts}
          columns={columns}
          rowKey="id"
          pagination={products.length > 10 ? { pageSize: 10 } : false}
          size={compact ? 'small' : 'middle'}
          className={styles.productTable}
          onRow={(record) => ({
            onClick: () => handleProductClick(record.id, record.name),
            className: isClickable ? styles.clickableRow : undefined
          })}
        />
      </div>
    );
  };

  const customDataFetcher =
    isDriveFlowReportingEnabled() ? (companyId: string) => fetchProductCatalogFromLocalBridge(companyId) : undefined;

  return (
    <BaseReportingComponent
      {...props}
      componentType="product_catalog"
      customDataFetcher={customDataFetcher}
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default ProductCatalogComponent;
